import { log } from './logging';
import { Database } from '../types/database.types';
import { toast } from 'react-toastify';
import { supabase } from '../lib/supabase';

export type OnboardingUser =
  Database['public']['Views']['OnboardingUsers']['Row'];

export async function fetchLeads(): Promise<OnboardingUser[]> {
  try {
    const { data, error } = await supabase.from('OnboardingUsers').select('*');

    if (error) {
      throw error;
    }

    return data || [];
  } catch (error) {
    log('Error fetching onboarding users', {}, error);
    throw error;
  }
}

export async function updateLeadField(
  id: string,
  field: string,
  value: string
): Promise<OnboardingUser> {
  const updatePromise = async () => {
    try {
      // Define fields that exist directly in the onboarding_session table
      const directFields = ['status', 'createdAt', 'updatedAt'];

      if (directFields.includes(field)) {
        // Update direct column in onboarding_session table
        const { error } = await supabase
          .from('onboarding_session')
          .update({ [field]: value })
          .eq('id', id)
          .select()
          .single();

        if (error) {
          throw error;
        }

        // Return the updated data in OnboardingUser format
        // We need to fetch from the view to get the complete user data
        const { data: viewData, error: viewError } = await supabase
          .from('OnboardingUsers')
          .select('*')
          .eq('id', id)
          .single();

        if (viewError) {
          throw viewError;
        }

        return viewData;
      } else {
        // Field should be updated in the stepData JSON
        // First, get the current stepData
        const { data: currentData, error: fetchError } = await supabase
          .from('onboarding_session')
          .select('stepData')
          .eq('id', id)
          .single();

        if (fetchError) {
          throw fetchError;
        }

        // Parse the current stepData or initialize as empty object
        const currentStepData =
          (currentData?.stepData as Record<string, unknown>) || {};

        // Update the specific field in stepData
        const updatedStepData = {
          ...currentStepData,
          [field]: value,
        };

        // Update the stepData in the database
        const { error } = await supabase
          .from('onboarding_session')
          .update({ stepData: updatedStepData })
          .eq('id', id)
          .select()
          .single();

        if (error) {
          throw error;
        }

        // Return the updated data from the OnboardingUsers view
        const { data: viewData, error: viewError } = await supabase
          .from('OnboardingUsers')
          .select('*')
          .eq('id', id)
          .single();

        if (viewError) {
          throw viewError;
        }

        return viewData;
      }
    } catch (error) {
      log('Error updating lead field', { id, field, value }, error);
      throw error;
    }
  };

  // Convert field name to human-readable format
  const fieldDisplayName = field
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .toLowerCase();

  return toast.promise(updatePromise(), {
    pending: `Updating ${fieldDisplayName}...`,
    success: `Successfully updated ${fieldDisplayName}`,
    error: {
      render({ data }: { data: Error }) {
        return `Failed to update ${fieldDisplayName}: ${data.message || 'Unknown error'}`;
      },
    },
  });
}
